import React from 'react';
import { Box, AppBar, Toolbar, Typography, Button, Container } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import {
  SyncAlt,
  Edit,
  CloudUpload,
  AudioFile,
  Share,
  Waves,
  Sync,
} from '@mui/icons-material';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box className="home-page">
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #3a4f61, #4a5f71)',
          boxShadow: '0 4px 12px rgba(56, 90, 124, 0.15)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: '5%' } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate('/')}
          >
            <img
              src="/auditio.png"
              alt="Auditio"
              style={{
                height: '32px',
                width: 'auto',
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Soria, serif',
                fontWeight: 400,
                fontSize: '1.5rem',
                color: 'white',
              }}
            >
              Auditio
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, gap: 4 }}>
              <Link to="/" style={{ color: 'white', textDecoration: 'none', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif' }}>
                Home
              </Link>
              <a
                href="https://forms.gle/C2jsBpdkhRMfVJh57"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white', textDecoration: 'none', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif' }}
              >
                Contact
              </a>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                background: 'linear-gradient(135deg, #f9967e, #fcb9aa)',
                color: 'white',
                borderRadius: '50px',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(249, 150, 126, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #fcb9aa, #f9967e)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(249, 150, 126, 0.4)',
                },
              }}
            >
              Sign In
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box className="hero-section">
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 5, py: 8, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, maxWidth: 500 }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Soria, serif',
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '3rem' },
                color: 'var(--text-color)',
                lineHeight: 1.2,
                mb: 3,
              }}
            >
              Edit, loop and share audio snippets with ease
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{
                  background: 'linear-gradient(135deg, #55cbcd, #3da3a5)',
                  color: 'white',
                  borderRadius: '50px',
                  px: 3.5,
                  py: 1.5,
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(85, 203, 205, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3da3a5, #55cbcd)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(85, 203, 205, 0.4)',
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1.2,
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid rgba(85, 203, 205, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2,
            }}
          >
            <img
              src="/audio-file-waveform.png"
              alt="Audio Waveform Editor and Snippets Table"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
            />
            <img
              src="/youtube-link-waveform.png"
              alt="Video Player Interface with Timeline Editor"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
            />
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box className="features-section">
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Soria, serif',
              fontWeight: 600,
              fontSize: '2rem',
              color: 'var(--text-color)',
              mb: 6,
            }}
          >
            Powerful Features for Audio Editing
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
              maxWidth: 1200,
              mx: 'auto',
            }}
          >
            {[
              { Icon: SyncAlt, title: 'Loop snippets of a track', desc: 'Easily select and loop specific sections of any audio track to create perfect practice loops or sound effects.' },
              { Icon: Edit, title: 'Create, update, delete snippets', desc: 'Full control over your audio snippets. Edit start/end points, update details, or delete unwanted segments.' },
              { Icon: CloudUpload, title: 'Upload from Computer, Google Drive or YouTube', desc: 'Import audio effortlessly from your local machine, cloud storage, or directly from YouTube links.' },
              { Icon: AudioFile, title: 'See all your uploaded files', desc: 'Access and manage your entire library of uploaded audio files and their corresponding snippets in one organized place.' },
              { Icon: Share, title: 'Share links with friends', desc: 'Collaborate and share your creations instantly by generating shareable links for your audio snippets.' },
              { Icon: Waves, title: 'Intuitive Waveform Editor', desc: 'Visualize your audio with a responsive waveform, making precise snippet selection straightforward and enjoyable.' },
            ].map((feature, idx) => (
              <Box
                key={idx}
                sx={{
                  background: 'white',
                  p: 4,
                  borderRadius: '12px',
                  textAlign: 'left',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(85, 203, 205, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                    borderColor: 'rgba(85, 203, 205, 0.2)',
                  },
                }}
              >
                <feature.Icon
                  sx={{
                    width: '24px',
                    height: '24px',
                    mb: 1.5,
                    color: '#55cbcd',
                    display: 'block',
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'var(--text-color)',
                    mb: 1,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--text-color)',
                    opacity: 0.7,
                    fontSize: '0.95rem',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {feature.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box className="how-it-works-section">
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Soria, serif',
              fontWeight: 600,
              fontSize: '2rem',
              color: 'var(--text-color)',
              mb: 6,
            }}
          >
            How It Works
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 5,
              flexWrap: 'wrap',
            }}
          >
            {[
              { Icon: CloudUpload, title: 'Upload Audio', desc: 'Import your tracks from various sources.', color: '#55cbcd' },
              { image: '/waveform.png', title: 'Create Snippet', desc: 'Select and define the perfect sections.' },
              { Icon: Sync, title: 'Loop & Share', desc: 'Share your loops with friends or export.', color: '#f9967e' },
            ].map((step, idx) => {
              const StepIcon = step.Icon;
              return (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      height: 140,
                      fontSize: '1.5rem',
                      color: '#55cbcd',
                      opacity: 0.6,
                    }}
                  >
                    →
                  </Box>
                )}
                <Box sx={{ flex: 1, maxWidth: 250, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    sx={{
                      background: 'white',
                      width: '100%',
                      height: 140,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      border: '1px solid rgba(85, 203, 205, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {step.image ? (
                      <img
                        src={step.image}
                        alt={step.title}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : StepIcon ? (
                      <StepIcon
                        sx={{
                          fontSize: '3rem',
                          color: step.color || '#55cbcd',
                        }}
                      />
                    ) : null}
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: 'Soria, serif',
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      color: 'var(--text-color)',
                      mb: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--text-color)',
                      opacity: 0.7,
                      fontSize: '0.95rem',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Box>
              </React.Fragment>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          background: 'rgba(255, 255, 255, 0.8)',
          py: 4,
          textAlign: 'center',
          borderTop: '1px solid rgba(85, 203, 205, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Link
            to="/privacy-policy"
            style={{
              color: 'var(--text-color)',
              textDecoration: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.95rem',
              opacity: 0.7,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms-of-service"
            style={{
              color: 'var(--text-color)',
              textDecoration: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.95rem',
              opacity: 0.7,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
          >
            Terms of Service
          </Link>
        </Box>
        <Typography
          sx={{
            color: 'var(--text-color)',
            opacity: 0.6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.95rem',
          }}
        >
          &copy; 2024 Auditio. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default HomePage;

